/**
 * useModelDropdownPanel - reusable open/close/position logic for
 * card-with-thumbnail model dropdown pickers (ImageModelSelector,
 * CheckpointPicker, ...).
 *
 * Ported verbatim from ImageModelSelector's original handleToggle/panelStyle
 * logic - this is a mechanical extraction, not a redesign. Owns only its own
 * open/position state; does NOT touch the model-list/search state that lives
 * in imageModelSelectorReducer.ts.
 */

import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type RefObject,
  type CSSProperties,
} from 'react';
import { useClickOutside } from '@/hooks/useClickOutside';

interface PanelPosition {
  left: number;
  width: number;
  openUp: boolean;
  /** CSS `bottom` when openUp=true; CSS `top` when openUp=false */
  offset: number;
}

const DEFAULT_PANEL_POS: PanelPosition = { left: 0, width: 300, openUp: true, offset: 0 };

export interface UseModelDropdownPanelResult {
  isOpen: boolean;
  panelStyle: CSSProperties;
  triggerRef: RefObject<HTMLButtonElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  toggle: () => void;
  close: () => void;
}

export function useModelDropdownPanel(disabled: boolean): UseModelDropdownPanelResult {
  const [isOpen, setIsOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<PanelPosition>(DEFAULT_PANEL_POS);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  // Toggle dropdown - calculate position ONCE on open
  const toggle = useCallback(() => {
    if (disabled) return;
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownMaxHeight = 440;
      const openUp = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

      setPanelPos({
        left: rect.left,
        width: rect.width,
        openUp,
        offset: openUp ? window.innerHeight - rect.top + 4 : rect.bottom + 4,
      });
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  // Lock the page's own scroll container while open, instead of closing on
  // the first scroll event. The panel is `position: fixed` relative to the
  // viewport and its coordinates are computed ONCE on open (not re-tracked),
  // so letting the background scroll while open let the trigger drift out
  // from under a now-stale fixed panel — visually "stuck"/detached — and any
  // incidental scroll (e.g. the search input's autofocus scrolling itself
  // into view) could fire the old close-on-scroll listener at the wrong
  // moment, reading as the dropdown flickering shut right after opening.
  useEffect(() => {
    if (!isOpen) return;
    const appContent = document.querySelector<HTMLElement>('.app-content');
    if (!appContent) return;
    const previousOverflow = appContent.style.overflow;
    appContent.style.overflow = 'hidden';
    return () => {
      appContent.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Close dropdown on outside click or Escape
  useClickOutside([triggerRef, panelRef], close, { enabled: isOpen, escape: true });

  // Panel style computed once on open - no re-computation = no glitches
  const panelStyle: CSSProperties = {
    position: 'fixed',
    zIndex: 9999,
    left: panelPos.left,
    width: panelPos.width,
    ...(panelPos.openUp
      ? { bottom: panelPos.offset, top: 'auto' }
      : { top: panelPos.offset, bottom: 'auto' }),
  };

  return { isOpen, panelStyle, triggerRef, panelRef, toggle, close };
}
